import moment from "moment";
import { useState, useEffect } from "react";
import { useMutation } from "react-query";
import useFetchData from "../../Hooks/useFetchData";
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead } from "../../Services/Notification";
import PageHeader from "../../Components/PageHeader";
import Pagination from "../../Components/Table/Pagination";
import "./Notifications.css";

const getNotificationIcon = (title = "") => {
  const value = title.toLowerCase();
  if (value.includes("signup") || value.includes("request")) return "fas fa-user-plus";
  if (value.includes("payout") || value.includes("payment")) return "fas fa-wallet";
  if (value.includes("ride") || value.includes("booking")) return "fas fa-truck-moving";
  if (value.includes("review") || value.includes("rating")) return "fas fa-star";
  return "fas fa-bell";
};

export default function Notifications() {
    const [filter, setFilter] = useState("all");
    const [now, setNow] = useState(Date.now());

    const { page, setPage, isLoading, data, refetch } = useFetchData(
        "get-notifications",
        (page, filterType) => getNotifications(page, 10, filterType),
        [filter]
    );

    useEffect(() => {
        setPage(1);
    }, [filter, setPage]);

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 60000);
        return () => clearInterval(id);
    }, []);

    const notifications = data?.data?.data?.docs || [];

    const { mutate: mutateMarkAsRead, isLoading: isMarkingRead } = useMutation(
        (id) => markNotificationAsRead(id),
        {
            onSuccess: () => refetch(),
        }
    );

    const { mutate: mutateMarkAllAsRead, isLoading: isMarkingAllRead } = useMutation(
        () => markAllNotificationsAsRead(),
        {
            onSuccess: () => refetch(),
        }
    );

    const hasUnread = notifications.some((item) => !item.read);

    return (
        <div className="app-content content dashboard">
            <div className="content-wrapper">
                <div className="content-body">
                    <section id="configuration">
                        <div className="user-details-wrapper notif-page">
                            <PageHeader
                                title="Notifications"
                                subtitle="View platform alerts and account activity updates"
                                className="notif-page-header"
                                action={
                                    <button
                                        type="button"
                                        className="notif-mark-all-btn"
                                        onClick={() => mutateMarkAllAsRead()}
                                        disabled={isMarkingAllRead || !hasUnread}
                                    >
                                        <i className="fas fa-check-double" />
                                        {isMarkingAllRead ? "Processing..." : "Mark All as Read"}
                                    </button>
                                }
                            />

                            <div className="notif-tabs">
                                <span className="notif-tabs__label">Show</span>
                                {["all", "unread", "new"].map((tab) => (
                                    <button
                                        key={tab}
                                        type="button"
                                        className={`notif-tabs__btn ${filter === tab ? "is-active" : ""}`}
                                        onClick={() => setFilter(tab)}
                                    >
                                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                    </button>
                                ))}
                            </div>

                            <div className="notif-list">
                                {notifications.length > 0 ? (
                                    notifications.map((item) => (
                                        <div
                                            className={`notif-row ${item.read ? "" : "is-unread"}`}
                                            key={item?._id}
                                        >
                                            <div className="notif-icon">
                                                <i className={getNotificationIcon(item?.notification?.title)} />
                                            </div>
                                            <div className="notif-copy">
                                                <h6 className="notif-copy__title">
                                                    {item?.notification?.title || "Notification"}
                                                    {!item.read && <span className="notif-unread-pill">New</span>}
                                                </h6>
                                                <p className="notif-copy__body">
                                                    {item?.notification?.body || "No additional details"}
                                                </p>
                                            </div>
                                            <div className="notif-meta">
                                                {!item.read && (
                                                    <button
                                                        type="button"
                                                        className="notif-read-btn"
                                                        onClick={() => mutateMarkAsRead(item?._id)}
                                                        disabled={isMarkingRead}
                                                    >
                                                        {isMarkingRead ? "..." : "Mark as read"}
                                                    </button>
                                                )}
                                                <span className="notif-meta__relative">
                                                    {moment(item?.createdAt).from(now)}
                                                </span>
                                                <span className="notif-meta__absolute">
                                                    {moment(item?.createdAt).format("DD MMM YYYY, hh:mm A")}
                                                </span>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div className="notif-empty">
                                        <i className="fas fa-bell-slash" />
                                        <p className="mb-0">{isLoading ? "Loading notifications..." : "No notifications found"}</p>
                                    </div>
                                )}
                            </div>

                            <div className="mt-4">
                                <Pagination
                                    currentDocs={notifications.length}
                                    totalDocs={data?.data?.data?.totalDocs}
                                    totalPages={Math.ceil(data?.data?.data?.totalPages || 0)}
                                    page={page}
                                    setPage={setPage}
                                />
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
